import { useMemo, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { DataGrid } from '@mui/x-data-grid';
import { Box } from '@mui/material';

const PAGE_SIZE_OPTIONS = [5, 10, 20];
const DEFAULT_PAGE_SIZE = 5;

export default function GeocoderResultsTable({ records = [], onSelectRecord, onRecordUpdate }) {
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: DEFAULT_PAGE_SIZE });
    const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

    const handleProcessRowUpdate = (newRow, oldRow) => {
        if (onRecordUpdate && JSON.stringify(newRow) !== JSON.stringify(oldRow)) {
            onRecordUpdate(newRow);
        }
        return newRow;
    };

    const handleProcessRowUpdateError = (error) => {
        console.error("Error processing row update:", error);
    };

    const memo = useMemo(() => {
        if (!records?.length) {
            return { columns: [], rows: [] };
        }
        
        const headerNameMap = {
            srid: '좌표계',
            input: '입력값',
            roadAddress: '도로명주소',
            jibunAddress: '지번주소',
            x: 'X',
            y: 'Y',
            timestamp: '실행시간',
        };

        const sample = records[0] || {};
        const columns = Object.keys(sample).map((key) => ({
            field: key,
            headerName: headerNameMap[key] || key,
            sortable: true,
            width: 180,
            editable: !['id', 'timestamp', 'layerIndex', 'x3857', 'y3857', 'source'].includes(key),
        }));
        const rows = records.map((record, idx) => ({
            id: record.id ?? idx,
            ...record,
        }));
        return { columns, rows };
    }, [records]);

    useEffect(() => {
        const isManual = records[0]?.source === 'manual';
        if (isManual) {
            const allKeys = Object.keys(records[0] || {});
            const keysToShow = ['srid', 'input', 'roadAddress', 'jibunAddress', 'x', 'y', 'timestamp'];
            const visibilityModel = {};
            allKeys.forEach(key => {
                if (!keysToShow.includes(key)) {
                    visibilityModel[key] = false;
                }
            });
            setColumnVisibilityModel(visibilityModel);
        } else {
            setColumnVisibilityModel({ mapX: false, mapY: false, id: false, __rowIndex: false });
        }
    }, [records]);

    if (!memo.rows.length) {
        return <div style={{ padding: 16, color: '#6b7280' }}>결과가 없습니다.</div>;
    }

    return (
        <div style={{ width: '100%', height: '100%', minHeight: 0 }}>
            <DataGrid
                rows={memo.rows}
                columns={memo.columns}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                pageSizeOptions={PAGE_SIZE_OPTIONS}
                pagination
                density="compact"
                disableRowSelectionOnClick
                onRowClick={(params) => onSelectRecord?.(params.row)}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={(newModel) => setColumnVisibilityModel(newModel)}
                processRowUpdate={handleProcessRowUpdate}
                onProcessRowUpdateError={handleProcessRowUpdateError}
                sx={{
                    border: 'none',
                    '& .MuiDataGrid-columnHeaders': {
                        backgroundColor: '#f3f4f6',
                        borderBottom: '1px solid #e0e0e0',
                    },
                    '& .MuiDataGrid-footerContainer': {
                        backgroundColor: '#f3f4f6',
                        borderTop: '1px solid #e0e0e0',
                    },
                }}
            />
        </div>
    );
}

GeocoderResultsTable.propTypes = {
    records: PropTypes.array,
    onSelectRecord: PropTypes.func,
    onRecordUpdate: PropTypes.func,
};

